import { Controller } from '@hotwired/stimulus'
import { useClickOutside } from 'stimulus-use'
import { computePosition, autoUpdate, offset, flip, shift, size } from '@floating-ui/dom'

// Unified dropdown/menu controller.
//
// One controller backs every action/navigation/filter menu in the app. The
// trigger toggles a panel that is positioned with Floating UI using the
// `fixed` strategy, which escapes `overflow` clipping (e.g. the board's
// scroll containers) WITHOUT moving the panel out of this controller's DOM
// subtree. Keeping the panel in place is deliberate: Stimulus binds action
// listeners within a controller's element, so teleporting the panel to
// <body> would silently break `menu#selectItem` and any host-controller
// actions on the items (e.g. `markdown-editor#insertTable`).
//
// Markup is produced by shared/_menu, _menu_item and _menu_section partials.
export default class extends Controller {
  static targets = ['trigger', 'content']
  static values = {
    placement: { type: String, default: 'bottom-start' },
    offset: { type: Number, default: 8 },
    closeOnSelect: { type: Boolean, default: true },
    matchWidth: { type: Boolean, default: false }
  }

  connect () {
    this.isOpen = false
    this.activeIndex = -1
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)
    useClickOutside(this)
  }

  disconnect () {
    this.stopAutoUpdate()
    document.removeEventListener('keydown', this.onDocumentKeydown, true)
  }

  // Actions ---------------------------------------------------------------

  toggle () {
    this.isOpen ? this.close() : this.open()
  }

  // ArrowDown/ArrowUp on the trigger opens the menu and moves focus inside.
  openViaKeyboard (event) {
    event.preventDefault()
    const toLast = event.key === 'ArrowUp'
    this.open()
    this.focusItem(toLast ? this.items().length - 1 : this.firstSelectableIndex())
  }

  // Bound on items. Only closes the menu — never preventDefault/stopPropagation,
  // so link navigation and button_to form submission (incl. turbo_confirm and
  // turbo_frame targets) proceed natively.
  selectItem () {
    if (this.closeOnSelectValue) this.close({ focusTrigger: false })
  }

  clickOutside (event) {
    if (!this.isOpen) return
    // The panel lives inside this.element (no teleport), so a normal
    // `contains` check already excludes clicks within it.
    this.close({ focusTrigger: false })
  }

  // Open / close ----------------------------------------------------------

  open () {
    if (this.isOpen) return
    this.isOpen = true

    const panel = this.contentTarget
    panel.style.position = 'fixed'
    panel.style.left = '0px'
    panel.style.top = '0px'
    panel.style.visibility = 'hidden'
    panel.classList.remove('hidden')

    this.triggerTarget.setAttribute('aria-expanded', 'true')
    this.startAutoUpdate()
    document.addEventListener('keydown', this.onDocumentKeydown, true)
  }

  close ({ focusTrigger = false } = {}) {
    if (!this.isOpen) return
    this.isOpen = false
    this.activeIndex = -1

    this.stopAutoUpdate()
    document.removeEventListener('keydown', this.onDocumentKeydown, true)

    const panel = this.contentTarget
    panel.classList.add('hidden')
    panel.style.visibility = ''

    this.triggerTarget.setAttribute('aria-expanded', 'false')
    if (focusTrigger) this.triggerTarget.focus()
  }

  // Positioning -----------------------------------------------------------

  startAutoUpdate () {
    this.cleanup = autoUpdate(this.triggerTarget, this.contentTarget, () => {
      computePosition(this.triggerTarget, this.contentTarget, {
        strategy: 'fixed',
        placement: this.placementValue,
        middleware: [
          offset(this.offsetValue),
          flip({ padding: 8 }),
          shift({ padding: 8 }),
          size({
            padding: 8,
            apply: ({ availableHeight, rects, elements }) => {
              Object.assign(elements.floating.style, {
                maxHeight: `${Math.max(160, Math.floor(availableHeight))}px`,
                minWidth: this.matchWidthValue ? `${Math.round(rects.reference.width)}px` : ''
              })
            }
          })
        ]
      }).then(({ x, y }) => {
        Object.assign(this.contentTarget.style, {
          left: `${Math.round(x)}px`,
          top: `${Math.round(y)}px`,
          visibility: 'visible'
        })
      })
    })
  }

  stopAutoUpdate () {
    if (this.cleanup) {
      this.cleanup()
      this.cleanup = null
    }
  }

  // Keyboard --------------------------------------------------------------

  onDocumentKeydown (event) {
    if (!this.isOpen) return

    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        event.stopPropagation()
        this.close({ focusTrigger: true })
        break
      case 'ArrowDown':
        event.preventDefault()
        this.focusItem(this.nextIndex(1))
        break
      case 'ArrowUp':
        event.preventDefault()
        this.focusItem(this.nextIndex(-1))
        break
      case 'Home':
        event.preventDefault()
        this.focusItem(0)
        break
      case 'End':
        event.preventDefault()
        this.focusItem(this.items().length - 1)
        break
      case 'Tab':
        this.close({ focusTrigger: false })
        break
    }
  }

  // Items -----------------------------------------------------------------

  items () {
    return Array.from(
      this.contentTarget.querySelectorAll('[role="menuitem"]:not([disabled])')
    )
  }

  firstSelectableIndex () {
    const items = this.items()
    const current = items.findIndex((el) => el.getAttribute('aria-current') === 'true')
    return current === -1 ? 0 : current
  }

  nextIndex (delta) {
    const count = this.items().length
    if (count === 0) return -1
    const from = this.activeIndex === -1 ? (delta > 0 ? -1 : 0) : this.activeIndex
    return (from + delta + count) % count
  }

  focusItem (index) {
    const items = this.items()
    if (items.length === 0) return
    const clamped = Math.max(0, Math.min(index, items.length - 1))
    items.forEach((el, i) => el.setAttribute('tabindex', i === clamped ? '0' : '-1'))
    this.activeIndex = clamped
    items[clamped].focus()
  }
}
